import {
  Button,
  ButtonGroup,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {RefObject} from 'react';

import {GetTransferOrderItemResponse, usePatchTransferOrderItemMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TransferOrderItemForm, TransferOrderItemFormSchema} from '../types/TransferOrderItemForm';
import {ArticleDetailCard} from './ArticleDetailCard';

interface TransferOrderItemDetailProps {
  transferOrderId: string;
  transferOrderItemId: string;
  item: GetTransferOrderItemResponse['transferOrderItem'];
  onClose: VoidFunction;
  onSubmit?: VoidFunction;
  isEditingDisabled?: boolean;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

export function TransferOrderItemDetail(props: TransferOrderItemDetailProps) {
  const [patchTransferOrderItem] = usePatchTransferOrderItemMutation({
    fixedCacheKey: 'edit-transfer-order-item',
  });

  const handleSubmit: FormSubmitHandler<TransferOrderItemForm> = async (formValues) => {
    await patchTransferOrderItem({
      transferOrderId: props.transferOrderId,
      transferOrderItemId: props.transferOrderItemId,
      body: {quantity: formValues.quantity},
    })
      .unwrap()
      .then(() => {
        if (props.isSavedByRowControlRef?.current) {
          props.isSavedByRowControlRef.current = false;
          return;
        }
        props.onClose();
      })
      .catch(handleApiError);
  };

  const defaultValues: TransferOrderItemForm = {
    quantity: props.item.quantity,
    serialNumber: props.item.serialNumber,
  };

  const dispensingUnit = props.item.article.dispensingUnit ?? 1;

  return (
    <Form<TransferOrderItemForm>
      isDisabled={props.isEditingDisabled}
      experimentalZodSchema={TransferOrderItemFormSchema(props.item.minMaxQuantity)}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        if (props.formRef) {
          props.formRef.current = {
            submit: () => control.submitForm(),
            isDirty: formApi.formState.isDirty,
          };
        }

        return (
          <VStack spacing={4}>
            <HStack spacing={4}>
              <Box width={35}>
                <FormField
                  isStepperVisible
                  shouldRoundStepsByDifference
                  step={dispensingUnit}
                  control={control}
                  name="quantity"
                  type="number"
                  label={i18n.t('entity.warehouse.labels.quantity')}
                  decimalPlaces={4}
                />
              </Box>
              <Box width={35}>
                <FormField
                  isDisabled
                  control={control}
                  name="serialNumber"
                  type="number"
                  label={i18n.t('entity.warehouse.labels.sNo')}
                />
              </Box>
            </HStack>
            <ArticleDetailCard
              warehouseId={props.item.article.warehouseId}
              articleId={props.item.article.id}
            />
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={i18n.t('general.actions.discard')}
                  onClick={props.onClose}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.saveChanges')}
                />
              </ButtonGroup>
            </DialogFooter>
          </VStack>
        );
      }}
    </Form>
  );
}
